import {useMemo} from 'react'

import {findByFieldName, normalizeFieldName} from '../../../../fields'
import {useQuantity} from '../../../../fields/quantity/useQuantity'
import {NOT_EDITABLE_FIELD} from '../../changeableFields/consts'
import {ChangeableFields} from '../../changeableFields/types'
import {useOrderConfirmationContext} from '../hooks/useOrderConfirmationContext'

/**
 * Custom hook that applies order confirmation-specific business rules to changeable fields.
 * This hook handles the special case where quantity changes are allowed within a restricted
 * range even after the normal quantity change deadline has passed, as long as the order
 * can still be confirmed/cancelled.
 *
 * @param baseChangeableFields - Base changeable fields from useBaseChangeableFieldsRules
 * @returns Modified changeable fields with order confirmation rules applied
 *
 * Business Logic:
 *  - When quantity changes are no longer auto-confirmable but order confirmation is still allowed,
 *    quantity-related fields become auto-confirmable if the value is within the confirmation range
 *  - This allows customers to make minor quantity adjustments during the confirmation window
 *    without requiring dispatcher approval
 *  - All other fields maintain their original state from base rules
 *
 * State Transitions:
 *  - requireDispatchConfirmation -> autoConfirmable (when within confirmation range)
 *  - notChangeable -> autoConfirmable (when within confirmation range)
 */
export const useOrderConfirmationChangeableFieldsRules = (
  baseChangeableFields: ChangeableFields
): ChangeableFields => {
  const {quantitySelection} = useQuantity()
  const orderConfirmation = useOrderConfirmationContext()

  return useMemo(() => {
    if (!orderConfirmation.isEnabled) {
      return baseChangeableFields
    }

    const {quantityRange} = orderConfirmation
    const quantityField =
      findByFieldName(baseChangeableFields, {field: 'quantity.volume'}) ?? NOT_EDITABLE_FIELD
    const isConfirmedOrderField =
      findByFieldName(baseChangeableFields, {field: 'isConfirmedOrder'}) ?? NOT_EDITABLE_FIELD

    // Don't apply confirmation rules if:
    // 1. Quantity is still auto-confirmable (normal change window active)
    // 2. Order confirmation is not auto-confirmable (confirmation window closed)
    if (
      quantityField.state === 'autoConfirmable' ||
      isConfirmedOrderField.state !== 'autoConfirmable'
    ) {
      return baseChangeableFields
    }

    // We're in the confirmation window: quantity changes require approval but order confirmation is allowed
    // Check if current quantity is within the allowed confirmation range
    const currentValue =
      quantitySelection.type === 'volume' ? quantitySelection.volume : quantitySelection.minQuantity // TODO: Check if using minQuantity for fullLoads is appropriate for confirmation rules, or if we need a different approach
    const isQuantityInConfirmationRange =
      currentValue >= quantityRange.min && currentValue <= quantityRange.max

    // Plus load has its own change deadline and must retain its independent approval state.
    const quantityRelatedFields = ['quantity', 'quantity.volume', 'quantity.quantityUom']

    return baseChangeableFields.map((changeableField) => {
      const {fieldName, isDirty} = changeableField
      const normalizedFieldName = normalizeFieldName(fieldName)

      // Only apply confirmation rules to quantity-related fields
      if (!quantityRelatedFields.includes(normalizedFieldName.field)) {
        return changeableField
      }

      // Promote to auto-confirmable if quantity is within confirmation range
      if (isQuantityInConfirmationRange) {
        return {
          fieldName,
          isDirty,
          state: 'autoConfirmable'
        }
      }

      // Keep original state if outside confirmation range
      return changeableField
    })
  }, [baseChangeableFields, quantitySelection, orderConfirmation])
}
